// NOTE: Please ensure this file does not import any files that import
// react as it's used directly in the workers.
import axios from "axios";
import moment from "moment";
import { isKeyOf, isRecord } from "../../../../types/internal/utility";
import type { LocalDatabasePaymentMethod } from "../../../_Generated/Sync.gen";
import type {
    LocalDatabaseSale,
    LocalDatabaseSaleItem,
    LocalDatabaseSaleItemPromotion,
    LocalDatabaseSaleItemType,
    LocalDatabaseSaleItemTypeGiftCard,
    LocalDatabaseSaleItemTypeProduct,
    LocalDatabaseSalePayment,
    LocalDatabaseSalePaymentStatus,
    SaleActionType,
    ServerSaleStatus,
} from "../../../Data/_Bases/BaseSalesRepository";
import { determineDataSource } from "../../../Data/DataStores/DataSource";
import {
    isActiveSurcharge,
    type SaleActiveSurcharge,
    type SalePaymentStatus,
    type SaleProduct,
    type SaleProductType,
    type SaleState,
    type SaleSurcharge,
} from "../../../Reducers/SellScreen/SellScreenSaleReducerTypes";
import NotFoundException from "../../../Routes/SellScreen/Exceptions/Data/NotFoundException";
import { scaleToDecimalPlaces } from "../../Generic/Numbers";
import { uppercaseFirst } from "../../Generic/Strings";
import UUID from "../../Generic/UUID";
import { getOption, setOption } from "../../Platform/Options/Options";
import { BarcodeTemplate } from "../Products/BarcodeTemplate";

interface SaleContext {
    registerId: string;
    userId: string;
}

function buildSaleItemBranch(
    branches: Array<string>,
    children: Record<string, Array<string>>,
    items: Record<string, GraphQLSaleItemContain>,
    quantityDivider: number
): Array<LocalDatabaseSaleItem> {
    const results: Array<LocalDatabaseSaleItem> = [];

    // Loop through the branches and create product details for it
    for(let i = 0; i < branches.length; i++) {
        const id = branches[i];
        const contain = items[id];

        let childBranches = children[id];

        if(!childBranches) {
            childBranches = [];
        }

        let item: Partial<LocalDatabaseSaleItemType> = {
            id  : contain.item.id,
            type: contain.item.type,
        };

        const containedItem = contain.item;
        let caseQuantity = 1;
        let taxRate = null;
        let taxRateAmount = 0;

        if(containedItem.type === "Product") {
            item = {
                ...item,
                name: containedItem.name,
            } as LocalDatabaseSaleItemTypeProduct;

            caseQuantity = containedItem.caseQuantity;
            taxRate = containedItem.taxRate?.id || null;
            taxRateAmount = containedItem.taxRate?.amount || 0;
        } else if(containedItem.type === "GiftCard") {
            item = {
                ...item,
                name: "Gift Card (" + containedItem.originalAmount + ")",
                code: containedItem.code,
                // amount: containedItem.originalAmount,
                // expiry: containedItem.expiry,
            } as LocalDatabaseSaleItemTypeGiftCard;
        }

        results.push({
            quantity    : Math.round(contain.quantity / quantityDivider * 100) / 100,
            caseQuantity: caseQuantity,
            loyaltyValue: 0,
            cost        : 0,
            note        : "",
            basePrice   : contain.price,
            discount    : {
                amount: "0",
                reason: "",
            },
            savings   : 0,
            tax       : taxRateAmount,
            taxRate   : taxRate,
            totalPrice: contain.price,
            promotions: [],
            item      : item as Required<LocalDatabaseSaleItemType>,
            products  : buildSaleItemBranch(childBranches, children, items, quantityDivider),
            priceList : false,
            metaData  : {},
        });
    }

    return results;
}

function buildSaleItemsTree(
    contains: Array<GraphQLSaleItemContain>,
    quantityDivider = 1
): Array<LocalDatabaseSaleItem> {
    // Map items to id
    const items: Record<string, GraphQLSaleItemContain> = {};
    const parents = [];
    const children: Record<string, Array<string>> = {};

    for(let i = 0; i < contains.length; i++) {
        const item = contains[i];

        // Skip if item has been deleted
        if(!item.item) {
            continue;
        }

        items[item.id] = item;

        if(!item.parentId) {
            parents.push(item.id);
        } else {
            if(!children[item.parentId]) {
                children[item.parentId] = [];
            }

            children[item.parentId].push(item.id);
        }
    }

    // Build tree from recursive function
    return buildSaleItemBranch(parents, children, items, quantityDivider);
}

export function transformGraphQLToIndexedDB<T extends undefined | GraphQLSale>(
    sale?: T,
    ignoreSurcharge = false
): T extends GraphQLSale ? LocalDatabaseSale : null {
    if(sale) {
        let items: Array<LocalDatabaseSaleItem> = [];

        if(Array.isArray(sale.items)) {
            items = sale.items.map(item => {
                let saleItem: Partial<LocalDatabaseSaleItemType> = {
                    type: item.item.type,
                };

                if(item.item.type === "Product") {
                    saleItem = {
                        type: "Product",
                        id  : item.item.id,
                        name: item.item.name,
                    } as LocalDatabaseSaleItemTypeProduct;
                } else if(item.item.type === "GiftCard") {
                    saleItem = {
                        type  : "GiftCard",
                        id    : item.item.id,
                        name  : "Gift Card (" + item.item.originalAmount + ")",
                        code  : item.item.code,
                        expiry: item.item.expiry,
                        source: "internal",
                    };
                } else if(item.item.type === "Surcharge" && ignoreSurcharge) {
                    return false;
                }

                return {
                    basePrice   : item.basePrice / item.quantity,
                    caseQuantity: item.caseQuantity,
                    cost        : item.cost,
                    discount    : {
                        ...item.discount,
                        amount: item.discount.amount.toString(),
                    },
                    item        : saleItem as Required<LocalDatabaseSaleItemType>,
                    loyaltyValue: item.loyaltyValue,
                    note        : item.note,
                    products    : buildSaleItemsTree(item.contains, item.quantity),
                    promotions  : item.promotions.map(promotion => ({
                        promotion     : promotion.promotion.id,
                        rebateAmount  : promotion.rebateAmount,
                        rebateQuantity: promotion.rebateQuantity,
                        savings       : promotion.savings,
                        quantity      : promotion.quantity,
                    })),
                    quantity  : item.quantity,
                    savings   : item.savings,
                    tax       : (item.taxRate ? item.taxRate.amount : 0),
                    taxRate   : (item.taxRate ? item.taxRate.id : null),
                    totalPrice: item.totalPrice,
                    metaData  : item.metaData,
                };
            }).filter(item => !!item) as Array<LocalDatabaseSaleItem>;
        }

        let payments: Array<LocalDatabaseSalePayment> = [];

        if(Array.isArray(sale.payments)) {
            payments = sale.payments.map(payment => ({
                amount         : payment.amount,
                cashout        : payment.cashout,
                metaData       : payment.metaData ?? {},
                paymentMethodId: payment.paymentMethod.id,
                processTime    : payment.processTime,
                registerId     : payment.register.id,
                rounding       : payment.rounding,
                status         : payment.status.toLowerCase() as LocalDatabaseSalePaymentStatus,
                receipt        : payment.receipt,
                subtype        : payment.subtype,
                cancelledAt    : payment.cancelledAt,
            }));
        }

        const formattedSale: LocalDatabaseSale = {
            actions: sale.actions.map(action => ({
                ...action,
                user     : action.user.id,
                affecting: action.affecting.id,
            })),
            cashout   : sale.cashout,
            clientId  : sale.clientId,
            createdAt : sale.createdAt,
            customerId: sale.customer ? sale.customer.id : null,
            discount  : {
                ...sale.discount,
                amount: sale.discount.amount.toString(),
            },
            id          : sale.id,
            invoiceId   : sale.invoiceId,
            items,
            linkedTo    : sale.linkedSale ? sale.linkedSale.id : null,
            loyaltyValue: sale.loyaltyValue,
            notes       : {
                internal: sale.internalNote,
                sale    : sale.note,
            },
            orderReference: sale.orderReference,
            refundReason  : sale.refundReason,
            payments,
            registerId    : sale.register ? sale.register.id : "",
            status        : sale.status,
            totalBase     : sale.totalBase,
            totalChange   : sale.totalChange,
            totalCost     : sale.totalCost,
            totalPrice    : sale.totalPrice,
            totalQuantity : sale.totalQuantity,
            totalRounding : sale.totalRounding,
            totalTax      : sale.totalTax,
            updatedAt     : sale.updatedAt,
            uploaded      : true,
            userId        : sale.user ? sale.user.id : "",
            metaData      : sale.metaData,
        };

        return formattedSale as T extends GraphQLSale ? LocalDatabaseSale : null;
    }

    return null as T extends GraphQLSale ? LocalDatabaseSale : null;
}

export const SALE_QUERY = `
    id,
    register {
        id,
    },
    user {
        id,
    },
    customer {
        id,
    },
    linkedSale {
        id,
    },
    status,
    clientId,
    invoiceId,
    note,
    internalNote,
    loyaltyValue,
    totalQuantity,
    totalCost,
    totalBase,
    totalTax,
    totalChange,
    totalRounding,
    totalPrice,
    cashout,
    orderReference,
    refundReason,
    discount {
        amount,
        reason,
    },
    items {
        quantity,
        caseQuantity,
        loyaltyValue,
        cost,
        note,
        basePrice,
        discount {
            amount,
            reason,
        },
        savings,
        taxRate {
            id,
            amount,
        },
        totalPrice,
        promotions {
            promotion {
                id,
            },
            rebateAmount,
            rebateQuantity,
            savings,
            quantity,
        },
        contains {
            id,
            parentId,
            saleItemId,
            item {
                type: __typename,
                id,
                ... on Product {
                    name,
                    caseQuantity,
                    taxRate {
                        id,
                        amount,
                    }
                },
                ... on GiftCard {
                    originalAmount,
                    code,
                    expiry,
                }
            }
            quantity,
            price,
        },
        item {
            type: __typename,
            id,
            ... on Product {
                name,
            }
            ... on GiftCard {
                code,
                originalAmount,
                expiry,
                clientId,
                status,
            }
        },
        metaData,
    },
    payments {
        paymentMethod {
            id,
        },
        register {
            id,
        }, 
        status,
        amount,
        rounding,
        cashout,
        processTime,
        metaData,
        receipt,
        subtype,
        cancelledAt,
    },
    metaData,
    updatedAt,
    createdAt,
    actions {
        action,
        value,
        affecting {
            id,
        },
        user {
            id,
        },
        timestamp,
    },
`;

interface GraphQLSaleItemContain {
    id: string;
    parentId: string;
    saleItemId: string;
    quantity: number;
    price: number;
    item: {
        type: "Product";
        id: string;
        name: string;
        caseQuantity: number;
        taxRate: null | {
            id: string;
            amount: number;
        };
    } | {
        type: "GiftCard";
        id: string;
        originalAmount: number;
        expiry: string;
        code: string;
    };
}

export interface GraphQLSale {
    id: string;
    register: {
        id: string;
    };
    user: {
        id: string;
    };
    customer: null | {
        id: string;
    };
    linkedSale: null | {
        id: string;
    };
    status: ServerSaleStatus;
    clientId: string;
    invoiceId: string;
    note: string;
    internalNote: string;
    loyaltyValue: number;
    totalQuantity: number;
    totalCost: number;
    totalBase: number;
    totalTax: number;
    totalChange: number;
    totalRounding: number;
    totalPrice: number;
    cashout: number;
    orderReference: string;
    refundReason: string;
    discount: {
        amount: number;
        reason: string;
    };
    items: Array<{
        quantity: number;
        caseQuantity: number;
        loyaltyValue: number;
        cost: number;
        note: string;
        basePrice: number;
        discount: {
            amount: number;
            reason: string;
        };
        savings: number;
        taxRate: null | {
            id: string;
            amount: number;
        };
        totalPrice: number;
        metaData: Record<string, unknown>;
        promotions: Array<{
            promotion: {
                id: string;
            };
            rebateAmount: number;
            rebateQuantity: number;
            savings: number;
            quantity: number;
        }>;
        contains: Array<GraphQLSaleItemContain>;
        item: {
            type: "Product";
            id: string;
            name: string;
        } | {
            type: "GiftCard";
            id: string;
            code: string;
            originalAmount: number;
            expiry: string;
            clientId: string;
            status: "ACTIVE" | "CANCELLED" | "FAILED";
        } | {
            type: "Surcharge";
        };
    }>;
    payments: Array<{
        paymentMethod: {
            id: string;
        };
        register: {
            id: string;
        };
        status: "COMPLETED" | "CANCELLED" | "FAILED";
        amount: number;
        rounding: number;
        cashout: number;
        processTime: string;
        metaData: unknown;
        receipt: string | null;
        subtype: string | null;
        cancelledAt: string | null;
    }>;
    updatedAt: string;
    createdAt: string;
    metaData: Record<string, unknown>;
    actions: Array<{
        timestamp: string;
        action: SaleActionType;
        value: string;
        user: {
            id: string;
        };
        affecting: {
            id: string;
        };
    }>;
}

export function getSaleFromServer(id: string): Promise<LocalDatabaseSale> {
    return new Promise((res, rej) => {
        axios.post("/graphql", {
            query: `query GetSale($id: ID!) {
                sale(id: $id) {
                    ${SALE_QUERY}
                }
            }`,
            variables: {
                id: id,
            },
        })
            .then(response => {
                if(response.data.errors) {
                    throw response.data.errors;
                }

                res(transformGraphQLToIndexedDB(response.data.data.sale) as LocalDatabaseSale);
            })
            .catch(err => {
                console.error("Could not get the sale from the server", err);
                rej(err);
            });
    });
}

async function formatContainedProduct(uuid: string, quantity: number | string): Promise<SaleProduct> {
    const source = await determineDataSource();
    const product = await source.products.get(uuid);

    if(typeof quantity === "string") {
        quantity = parseFloat(quantity);
    }

    let isCase;

    if(product.case_quantity !== 1 && quantity % product.case_quantity === 0) {
        isCase = true;
        quantity = quantity / product.case_quantity;
    } else {
        isCase = false;
    }

    // Get any additional products
    const additionalProducts = [];

    if(Array.isArray(product.components) && product.components.length) {
        for(let i = 0, l = product.components.length; i < l; i++) {
            if(product.components[i].quantity !== 0) {
                additionalProducts.push(
                    formatContainedProduct(
                        product.components[i].uuid,
                        product.components[i].quantity
                    )
                );
            }
        }
    }

    const containedProducts = await Promise.all(additionalProducts);

    return {
        lineId      : UUID.generate(),
        uuid        : product.uuid,
        type        : uppercaseFirst(product.type) as SaleProductType,
        name        : product.name,
        caseQuantity: product.case_quantity,
        isCase      : isCase,
        quantity    : quantity,
        loyalty     : {
            earn  : 0,
            redeem: 0,
        },
        prices: {
            price          : 0,
            unlockedPrice  : 0,
            normal         : 0,
            base           : 0,
            addPrice       : 0,
            removePrice    : 0,
            additionalPrice: [ 0, 0 ],
        },
        tax: {
            id    : product.tax_rate ? product.tax_rate.uuid : false,
            amount: product.tax_rate ? product.tax_rate.amount : 0,
        },
        note                 : "",
        products             : containedProducts,
        defaultProducts      : containedProducts,
        preventManualDiscount: product.prevent_manual_discounts,
        special              : product.request_price,
        edited               : false,
        promotions           : {},
        userId               : null,
        deleted              : false,
        initiallyDeleted     : false,
        familyId             : product.family_id,
        categoryId           : product.category_id,
        tags                 : product.tags,
        canUnlock            : !product.request_price,
        rebates              : {},
        giftCard             : null,
        discountReason       : "",
        lockQuantity         : true,
        surcharge            : null,
        metaData             : {},
        priceList            : false,
    };
}

export async function formatIndexedDBProduct(
    product: LocalDatabaseSaleItem,
    reloading = false,
    locked = true,
    giftCardCallback?: () => void,
    canUnlock = false,
    potentialBarcodeTemplates: Record<string, Record<string, BarcodeTemplate>> = {},
    restoreMetaData = false
): Promise<SaleProduct> {
    if(product.item.type === "Surcharge") {
        throw new Error("Cannot format a surcharge item as a product, this should have been formatted previously");
    }

    let localProduct;

    try {
        if(product.item.type === "Product") {
            const source = await determineDataSource();

            localProduct = await source.products.get(product.item.id);
        }
    } catch(err) {
        console.error("Error getting local product", err);
    }

    let basePrice = product.basePrice;
    const productType = product.item.type === "GiftCard" ?
        "GiftCard" :
        (localProduct?.type === "normal" ? "product" : localProduct?.type) ||
        "product";

    if(localProduct && localProduct.request_price) {
        basePrice = product.totalPrice;
    }

    // Check to see if this product was probably added to the sale via barcode template
    let barcodeTemplateMatched = false;

    if(localProduct && Object.keys(potentialBarcodeTemplates).length) {
        for(const barcode of localProduct.barcodes) {
            if(barcode.template === null) {
                continue;
            }

            const barcodeMatching = potentialBarcodeTemplates[barcode.template];

            if(typeof barcodeMatching === "undefined") {
                continue;
            }

            const groupedTemplates = Object.values(barcodeMatching);

            for(const template of groupedTemplates) {
                if(template.id !== barcode.code) {
                    // Wrong barcode to match
                    continue;
                }

                if(!template.price && !template.quantity) {
                    // Invalid barcode template
                    continue;
                }

                const barcodeQuantity = parseFloat(barcode.quantity);

                if(template.price) {
                    if(template.price * Math.sign(barcodeQuantity) !== product.totalPrice) {
                        // Prices don't match
                        continue;
                    }
                }

                if(template.recalculateQuantity) {
                    if(!template.price) {
                        // Invalid barcode template
                        continue;
                    }

                    if(
                        product.quantity !==
                        Math.round((template.price / basePrice * barcodeQuantity) * 10000) / 10000
                    ) {
                        // Recalculated quantity doesn't match
                        continue;
                    }
                } else if(template.quantity) {
                    if(template.quantity * barcodeQuantity !== product.quantity) {
                        continue;
                    }
                }

                barcodeTemplateMatched = true;
                break;
            }

            if(barcodeTemplateMatched) {
                break;
            }
        }
    }

    let discountAmount = parseFloat(product.discount.amount);

    if(isNaN(discountAmount)) {
        discountAmount = 0;
    }

    let productSurcharge: null | SaleActiveSurcharge = null;

    if(isRecord(product.metaData) && "surcharge" in product.metaData) {
        if(isActiveSurcharge(product.metaData.surcharge)) {
            productSurcharge = product.metaData.surcharge;
        }
    }

    const results: SaleProduct = {
        lineId      : UUID.generate(),
        uuid        : product.item.id,
        type        : uppercaseFirst(productType),
        name        : product.item.type === "GiftCard" ? `Gift Card (${product.item.code})` : product.item.name,
        caseQuantity: product.caseQuantity,
        isCase      : false,
        quantity    : product.quantity,
        loyalty     : {
            earn  : product.loyaltyValue,
            redeem: 0,
        },
        prices: {
            price          : product.totalPrice,
            unlockedPrice  : product.totalPrice + discountAmount,
            normal         : productType === "component" ? 0 : basePrice * product.quantity,
            base           : basePrice,
            addPrice       : 0,
            removePrice    : 0,
            additionalPrice: [ 0, 0 ],
        },
        tax: {
            id    : product.taxRate === null ? false : product.taxRate,
            amount: product.tax,
        },
        note                 : product.note,
        products             : [],
        defaultProducts      : [],
        preventManualDiscount: localProduct?.prevent_manual_discounts ?? false,
        special              : !!(product.item.type === "GiftCard" || (localProduct && localProduct.request_price)),
        edited               : locked,
        promotions           : {},
        discountReason       : product.discount ? product.discount.reason : "",
        canUnlock            : canUnlock && !(localProduct && localProduct.request_price) && !barcodeTemplateMatched,
        rebates              : {},
        userId               : null,
        familyId             : localProduct?.family_id ?? null,
        categoryId           : localProduct?.category_id ?? null,
        tags                 : localProduct?.tags ?? [],
        giftCard             : null,
        surcharge            : productSurcharge,
        priceList            : product.priceList,
        lockQuantity         : product.item.type === "GiftCard" || barcodeTemplateMatched,
        metaData             : restoreMetaData ? product.metaData : {},
    };

    if(!reloading) {
        // Will be automatically reloaded anyway
        for(let i = 0, l = product.promotions.length; i < l; i++) {
            results.promotions[product.promotions[i].promotion] = {
                name    : product.promotions[i].promotion,
                active  : false,
                quantity: product.promotions[i].quantity,
            };
        }
    }

    // Check for cases
    if(product.quantity % product.caseQuantity === 0 && product.caseQuantity !== 1) {
        results.isCase = true;
    }

    // Add gift card fields
    if(product.item.type === "GiftCard" && !reloading) {
        results.giftCard = {
            code  : product.item.code,
            amount: product.totalPrice,
            expiry: product.item.expiry,
            source: product.item.source,
        };
    }

    const products = [];

    for(let i = 0; i < product.products.length; i++) {
        const prod = product.products[i];

        if(product.item.type === "GiftCard") {
            if(typeof giftCardCallback === "function") {
                giftCardCallback();
            }

            continue;
        }

        products.push(formatIndexedDBProduct(
            prod,
            reloading,
            locked,
            giftCardCallback,
            canUnlock,
            {},
            restoreMetaData
        ));
    }

    results.products = await Promise.all(products);

    try {
        if(product.item.type !== "GiftCard") {
            const defaultProducts = [];

            if(localProduct) {
                for(let i = 0, l = localProduct.components.length; i < l; i++) {
                    if(localProduct.components[i].quantity !== 0) {
                        defaultProducts.push(
                            formatContainedProduct(
                                localProduct.components[i].uuid,
                                localProduct.components[i].quantity
                            )
                        );
                    }
                }
            }

            results.defaultProducts = await Promise.all(defaultProducts);

            // Ensure we calculate the price correctly for any modified children
            const handled: Array<number> = [];

            for(let i = 0, l = results.products.length; i < l; i++) {
                let index = -1;

                for(let j = 0, n = results.defaultProducts.length; j < n; j++) {
                    if(handled.includes(j)) {
                        continue;
                    }

                    if(results.products[i].uuid !== results.defaultProducts[j].uuid) {
                        continue;
                    }

                    index = j;
                    handled.push(j);
                    break;
                }

                results.prices.additionalPrice[1] += results.products[i].prices.price * product.quantity;

                if(index === -1) {
                    results.prices.additionalPrice[0] += results.products[i].prices.price * product.quantity;
                    results.products[i].prices.addPrice = results.products[i].prices.price;
                    results.products[i].prices.removePrice = 0;

                    continue;
                }

                const original = results.defaultProducts[index];

                if(original.prices.price !== results.products[i].prices.price) {
                    results.prices.unlockedPrice -= results.products[i].prices.price;
                }
            }

            for(let i = 0, l = results.defaultProducts.length; i < l; i++) {
                if(handled.includes(i)) continue;

                results.products.unshift({
                    ...results.defaultProducts[i],
                    deleted: true,
                });
            }
        }
    } catch(err) {
        console.error("Error getting contained products", err);
    }

    return results;
}

const SALE_PAYMENT_STATUS_MAP = {
    COMPLETED: "completed",
    CANCELLED: "cancelled",
    FAILED   : "failed",
    completed: "completed",
    cancelled: "cancelled",
    failed   : "failed",
};

export async function transformIndexedDBToSaleState(
    sale: LocalDatabaseSale,
    canUnlock = false,
    locked = true,
    restoreProductMetaData = false
): Promise<SaleState> {
    let priceSet = getOption("register_default_price_set");

    if(priceSet === null || priceSet === "default") {
        priceSet = "";
    }

    const results: SaleState = {
        internalId    : sale.id,
        id            : sale.id,
        linkedTo      : sale.linkedTo === null ? false : sale.linkedTo,
        invoiceId     : sale.invoiceId,
        status        : sale.status as ServerSaleStatus,
        products      : [],
        customer      : false,
        payments      : [],
        notes         : sale.notes,
        orderReference: sale.orderReference || "",
        refundReason  : sale.refundReason || "",
        productDetails: {
            id   : [],
            index: [],
        },
        totals: {
            sale     : sale.totalPrice,
            remaining: sale.totalPrice,
            paid     : 0,
            savings  : 0,
            discount : 0,
            change   : sale.totalChange,
            cashout  : 0,
            rounding : sale.totalRounding,
            surcharge: 0,
        },
        registerId    : sale.registerId,
        userId        : sale.userId,
        createdAt     : sale.createdAt,
        calculate     : false,
        addingToSale  : null,
        actions       : sale.actions,
        surchargeRules: [],
        surcharge     : null,
        metaData      : sale.metaData,
        isCancellable : sale.isCancellable ?? true,
        priceSet,
    };

    // Add Customer
    if(sale.customerId) {
        results.customer = {
            uuid: sale.customerId,
        };
    }

    // Map the potential barcode map by products
    const productTemplateMap: Record<string, Record<string, Record<string, BarcodeTemplate>>> = {};
    const barcodeTemplateMap: Record<string, Array<BarcodeTemplate>> = {};

    for(let i = 0, l = sale.actions.length; i < l; i++) {
        if(sale.actions[i].action !== "PRODUCT_SCAN") {
            continue;
        }

        const product = sale.actions[i].affecting;

        if(typeof productTemplateMap[product] === "undefined") {
            productTemplateMap[product] = {};
        }

        const barcode = sale.actions[i].value;

        if(typeof barcodeTemplateMap[barcode] === "undefined") {
            barcodeTemplateMap[barcode] = await BarcodeTemplate.getMatchingTemplates(barcode);
        }

        const barcodeTemplates = barcodeTemplateMap[barcode];

        for(const template of barcodeTemplates) {
            if(typeof productTemplateMap[product][template.name] === "undefined") {
                productTemplateMap[product][template.name] = {};
            }

            productTemplateMap[product][template.name][barcode] = template;
        }
    }

    // Add Products
    const products = [];

    for(let i = 0, l = sale.items.length; i < l; i++) {
        const product = sale.items[i];

        if(product.item.type === "Surcharge") {
            const surcharge = product.metaData as SaleSurcharge;

            if(!surcharge.appliesTo) {
                results.surcharge = {
                    id    : surcharge.id,
                    amount: product.totalPrice,
                };
            }

            results.surchargeRules.push(surcharge);
            results.totals.surcharge += product.totalPrice;
            continue;
        }

        const discount = parseFloat(product.discount.amount);

        results.totals.savings += product.savings;

        if(!isNaN(discount)) {
            results.totals.discount += discount;
        }

        products.push(formatIndexedDBProduct(
            product,
            false,
            locked,
            undefined,
            canUnlock,
            productTemplateMap[product.item.id],
            restoreProductMetaData
        ));
    }

    results.products = await Promise.all(products);

    const source = await determineDataSource();
    const paymentMethods = await source.paymentMethods.all();

    // Get cash out payment methods
    const cashoutMethods: Record<string, string> = {};
    const methodTypes: Record<string, string> = {
        loyalty: "loyalty",
    };

    for(let i = 0, l = paymentMethods.length; i < l; i++) {
        if(paymentMethods[i].allow_cash_out) {
            cashoutMethods[paymentMethods[i].uuid] = paymentMethods[i].cashout_method_id as string;
        }

        methodTypes[paymentMethods[i].uuid] = paymentMethods[i].type;
    }

    // Prepare search for reverted payments
    const payments = sale.payments;
    const methods: Record<string, Record<number, number>> = {};
    const cashout: Record<string, Record<number, number>> = {};

    for(let i = 0, l = payments.length; i < l; i++) {
        if(payments[i].paymentMethodId === "01000000000000000000000000000000") {
            payments[i].paymentMethodId = "loyalty";
        }

        if(payments[i].status.toLowerCase() === "completed") {
            if(!methods[payments[i].paymentMethodId]) {
                methods[payments[i].paymentMethodId] = {};
            }

            if(!methods[payments[i].paymentMethodId][payments[i].amount]) {
                methods[payments[i].paymentMethodId][payments[i].amount] = 0;
            }

            methods[payments[i].paymentMethodId][payments[i].amount]++;

            if(payments[i].cashout) {
                if(!cashout[cashoutMethods[payments[i].paymentMethodId]]) {
                    cashout[cashoutMethods[payments[i].paymentMethodId]] = {};
                }

                if(!cashout[cashoutMethods[payments[i].paymentMethodId]][(-payments[i].cashout)]) {
                    cashout[cashoutMethods[payments[i].paymentMethodId]][(-payments[i].cashout)] = 0;
                }

                cashout[cashoutMethods[payments[i].paymentMethodId]][(-payments[i].cashout)]++;
            }
        }
    }

    // Add payments
    for(let i = 0, l = payments.length; i < l; i++) {
        const payment = payments[i];

        let reverted = false;

        if(payment.status.toLowerCase() === "completed") {
            if(cashout[payment.paymentMethodId]) {
                if(cashout[payment.paymentMethodId][payment.amount] > 0) {
                    // Make assumption that this is a cashout payment
                    cashout[payment.paymentMethodId][payment.amount]--;
                    continue;
                }
            }

            if(methods[payment.paymentMethodId][(-payment.amount)] > 0) {
                methods[payment.paymentMethodId][(-payment.amount)]--;
                reverted = true;
            }
        }

        results.totals.remaining -= payment.amount;
        results.totals.paid += payment.amount;
        results.totals.cashout += payment.cashout;

        results.payments.push({
            method    : payment.paymentMethodId,
            type      : methodTypes[payment.paymentMethodId],
            subtype   : payment.subtype ?? undefined,
            registerId: payment.registerId,
            status    : SALE_PAYMENT_STATUS_MAP[payment.status] as SalePaymentStatus,
            amount    : payment.amount - payment.cashout,
            rounding  : payment.rounding,
            cashout   : payment.cashout,
            metadata  : payment.metaData ?
                (typeof payment.metaData === "string" ? payment.metaData : JSON.stringify(payment.metaData)) :
                payment.metadata ?
                    (typeof payment.metadata === "string" ? payment.metadata : JSON.stringify(payment.metadata)) :
                    "",
            processTime: moment(payment.processTime),
            uploaded   : true,
            receipt    : payment.receipt || undefined,
            cancelledAt: payment.cancelledAt ? new Date(payment.cancelledAt) : null,
            reverted,
        });
    }

    return results;
}

function formatSaleStateProduct(
    product: SaleProduct,
    loyalty: "earn" | "redeem",
    surchargesActive: boolean
): LocalDatabaseSaleItem {
    let metaData = product.metaData;

    if(surchargesActive && product.surcharge) {
        metaData = {
            ...(metaData ?? {}),
            surcharge: product.surcharge,
        };
    }

    const result: LocalDatabaseSaleItem = {
        basePrice   : product.prices.base,
        caseQuantity: product.caseQuantity,
        discount    : {
            reason: "",
            amount: "0",
        },
        item: {
            id  : product.uuid,
            type: product.type !== "GiftCard" ? "Product" : product.type,
            name: product.name,
        } as LocalDatabaseSaleItemTypeProduct,
        loyaltyValue: product.loyalty[loyalty],
        note        : product.note,
        products    : [],
        promotions  : [],
        quantity    : product.quantity,
        tax         : product.tax.amount,
        taxRate     : product.tax.id === false ? null : product.tax.id,
        totalPrice  : product.prices.price,
        savings     : 0,
        priceList   : product.priceList,
        metaData,
    };

    if(typeof product.prices.additionalPrice === "undefined") {
        product.prices.additionalPrice = [ 0, 0 ];
    }

    // Add the discount and savings information
    let hasDiscount = false;

    if(product.type !== "Voucher" && product.edited && product.canUnlock && !product.priceList) {
        const unlocked = scaleToDecimalPlaces(product.prices.unlockedPrice);
        const actual = scaleToDecimalPlaces(product.prices.price);

        if(unlocked !== actual) {
            result.discount.amount = scaleToDecimalPlaces(unlocked - actual).toString();
            hasDiscount = true;

            if(typeof product.discountReason !== "undefined") {
                result.discount.reason = product.discountReason;
            }
        }
    }

    // Add gift card information
    if(product.giftCard) {
        result.item = {
            ...result.item,
            ...product.giftCard,
        };
    }

    // Adjust promotion data
    const promotions: Record<string, LocalDatabaseSaleItemPromotion & {
        applied?: number;
    }> = {};
    let promotionalQuantity = 0;

    for(const i in product.promotions) {
        if(!Object.prototype.hasOwnProperty.call(product.promotions, i)) continue;

        if(!product.promotions[i].active || hasDiscount) {
            continue;
        }

        if(typeof promotions[i] === "undefined") {
            promotions[i] = {
                promotion     : i,
                rebateAmount  : 0,
                rebateQuantity: 0,
                savings       : 0,
                quantity      : 0,
                applied       : 0,
            };
        }

        promotions[i].quantity += 1;
        (promotions[i].applied as number) += product.promotions[i].quantity;

        promotionalQuantity += product.promotions[i].quantity;
    }

    for(const i in product.rebates) {
        if(!Object.prototype.hasOwnProperty.call(product.rebates, i)) continue;

        if(typeof promotions[i] === "undefined") {
            promotions[i] = {
                promotion     : i,
                rebateAmount  : 0,
                rebateQuantity: 0,
                savings       : 0,
                quantity      : 0,
            };
        }

        promotions[i].rebateAmount += product.rebates[i].amount;
        promotions[i].rebateQuantity += product.rebates[i].quantity;
    }

    // Distribute the savings
    let savings = 0;

    if(product.type !== "Voucher" && !hasDiscount) {
        const normal = Math.round(product.prices.normal * 100) / 100;
        const actual = Math.round((product.prices.price - product.prices.additionalPrice[1]) * 100) / 100;

        savings = normal - actual;
    }

    // Loop through the promotions and distribute the savings
    if(savings !== 0) {
        const savingsPerQuantity = savings / promotionalQuantity;
        let runningSavingsTotal = 0;

        for(const i in promotions) {
            if(!Object.prototype.hasOwnProperty.call(promotions, i)) {
                continue;
            }

            const promo = promotions[i];

            if(promo.applied === 0) {
                delete promo.applied;
                continue;
            } else if(typeof promo.applied === "undefined") {
                continue;
            }

            const promotionalSavings = Math.round(savingsPerQuantity * promo.applied * 100) / 100;

            promo.savings += promotionalSavings;

            delete promo.applied;

            runningSavingsTotal += promotionalSavings;
        }

        const difference = (Math.round(runningSavingsTotal * 100) / 100) - (Math.round(savings * 100) / 100);

        if(difference !== 0) {
            for(const i in promotions) {
                if(!Object.prototype.hasOwnProperty.call(promotions, i)) {
                    continue;
                }

                if(promotions[i].savings !== 0) {
                    promotions[i].savings += difference;
                    break;
                }
            }
        }
    }

    result.promotions = Object.values(promotions);

    for(let i = 0; i < product.products.length; i++) {
        const prod = product.products[i];

        if(prod.deleted && !prod.initiallyDeleted) {
            continue;
        }

        result.products.push(formatSaleStateProduct(prod, loyalty, surchargesActive));
    }

    return result;
}

export function transformSaleStateToIndexedDB(sale: Required<SaleState>, context: SaleContext): LocalDatabaseSale {
    let createdAt = sale.createdAt;

    if(moment.isMoment(createdAt)) {
        createdAt = createdAt.toISOString();
    }

    // Prepare results
    const results: LocalDatabaseSale = {
        id        : sale.id,
        linkedTo  : sale.linkedTo ? sale.linkedTo : null,
        cashout   : 0,
        createdAt,
        customerId: sale.customer ? sale.customer.uuid : null,
        discount  : {
            reason: "",
            amount: "0",
        },
        notes         : sale.notes,
        orderReference: sale.orderReference,
        refundReason  : sale.refundReason,
        invoiceId     : sale.invoiceId,
        items         : [],
        payments      : [],
        registerId    : context.registerId,
        status        : sale.status,
        loyaltyValue  : 0,
        totalBase     : 0,
        totalChange   : 0,
        totalCost     : 0,
        totalPrice    : 0,
        totalQuantity : 0,
        totalRounding : 0,
        totalTax      : 0,
        updatedAt     : moment().toISOString(true),
        userId        : context.userId,
        uploaded      : false,
        actions       : sale.actions,
        metaData      : sale.metaData ?? {},
        isCancellable : sale.isCancellable,
    };

    const loyalty = "earn";

    const payments = sale.payments;
    let paymentLoyaltyPoints = 0;

    for(let i = 0, l = payments.length; i < l; i++) {
        const payment = payments[i];

        let metadata: string | null = payment.metadata;

        if(metadata) {
            if(metadata === "") {
                metadata = null;
            } else {
                const md = JSON.parse(metadata);

                if(md.loyalty_points && payment.status === "completed") {
                    paymentLoyaltyPoints += md.loyalty_points;
                }
            }
        }

        const salePayment: LocalDatabaseSalePayment = {
            amount         : payment.amount,
            metadata       : metadata,
            cashout        : payment.cashout,
            rounding       : payment.rounding || 0,
            paymentMethodId: payment.method,
            registerId     : payment.registerId,
            status         : payment.status as "cancelled" | "completed" | "failed",
            processTime    : moment(payment.processTime).toISOString(true),
            receipt        : payment.receipt || null,
            subtype        : payment.subtype ?? null,
            cancelledAt    : (
                payment.cancelledAt instanceof Date ? payment.cancelledAt.toISOString() : (payment.cancelledAt ?? null)
            ),
        };

        results.cashout += payment.cashout;

        results.payments.push(salePayment);
    }

    if(isNaN(paymentLoyaltyPoints)) {
        paymentLoyaltyPoints = 0;
    }

    const surchargesActive = sale.surchargeRules && sale.totals.surcharge !== 0 && sale.status !== "PARKED";
    const products = sale.products;
    let productLoyaltyPoints = 0;
    let productRedeemPoints = 0;

    for(let i = 0, l = products.length; i < l; i++) {
        const product = products[i];

        results.totalPrice += product.prices.price;
        results.totalQuantity += product.quantity;
        productLoyaltyPoints += product.loyalty.earn;
        productRedeemPoints += product.loyalty.redeem;

        results.items.push(formatSaleStateProduct(
            product,
            loyalty,
            surchargesActive
        ));
    }

    if(surchargesActive) {
        // Prepare the map of the rules
        const surchargeRuleMap: Record<number, SaleSurcharge> = {};

        for(const rule of sale.surchargeRules) {
            surchargeRuleMap[rule.id] = rule;
        }

        // Prepare the product surcharges
        const surchargeMap: Record<number, number> = {};

        for(const product of sale.products) {
            if(!product.surcharge) {
                continue;
            }

            if(product.surcharge.id in surchargeMap) {
                surchargeMap[product.surcharge.id] += product.surcharge.amount;
            } else {
                surchargeMap[product.surcharge.id] = product.surcharge.amount;
            }
        }

        for(const id in surchargeMap) {
            if(!isKeyOf(surchargeMap, id)) {
                continue;
            }

            const amount = surchargeMap[id];

            results.items.push({
                basePrice   : amount,
                caseQuantity: 1,
                cost        : amount,
                discount    : {
                    amount: "0",
                    reason: "",
                },
                item: {
                    type: "Surcharge",
                },
                loyaltyValue: 0,
                note        : "",
                products    : [],
                promotions  : [],
                quantity    : 1,
                savings     : 0,
                tax         : 0, // This will be calculated on the server
                taxRate     : surchargeRuleMap[id]?.taxRate ?? null,
                totalPrice  : amount,
                metaData    : surchargeRuleMap[id] ?? {},
                priceList   : false,
            });
        }

        if(sale.surcharge) {
            results.items.push({
                basePrice   : sale.surcharge.amount,
                caseQuantity: 1,
                cost        : sale.surcharge.amount,
                discount    : {
                    amount: "0",
                    reason: "",
                },
                item: {
                    type: "Surcharge",
                },
                loyaltyValue: 0,
                note        : "",
                products    : [],
                promotions  : [],
                quantity    : 1,
                savings     : 0,
                tax         : 0, // This will be calculated on the server
                taxRate     : surchargeRuleMap[sale.surcharge.id].taxRate,
                totalPrice  : sale.surcharge.amount,
                metaData    : surchargeRuleMap[sale.surcharge.id] ?? {},
                priceList   : false,
            });
        }
    }

    if(isNaN(productLoyaltyPoints)) {
        productLoyaltyPoints = 0;
    }

    if(isNaN(productRedeemPoints)) {
        productRedeemPoints = 0;
    }

    if(productRedeemPoints !== 0) {
        results.loyaltyValue = paymentLoyaltyPoints === 0 ?
            productLoyaltyPoints :
            productLoyaltyPoints - (paymentLoyaltyPoints / productRedeemPoints * productLoyaltyPoints);
    } else {
        results.loyaltyValue = productLoyaltyPoints;
    }

    if(productLoyaltyPoints !== 0) {
        const loyaltyDistribution = results.loyaltyValue / productLoyaltyPoints;

        if(loyaltyDistribution !== 1) {
            for(let i = 0, l = results.items.length; i < l; i++) {
                results.items[i].loyaltyValue *= loyaltyDistribution;
            }
        }
    }

    return results;
}

function formatSaleItemContains(products: Array<LocalDatabaseSaleItem>): Array<{
    id: string;
    quantity: number;
    contains: any;
    price: number;
}> {
    const result = [];

    for(let i = 0, l = products.length; i < l; i++) {
        const prod = products[i];

        if(prod.item.type === "Surcharge") {
            continue;
        }

        result.push({
            id      : prod.item.id,
            quantity: prod.quantity,
            contains: formatSaleItemContains(prod.products),
            price   : prod.totalPrice,
        });
    }

    return result;
}

export function transformIndexedDBToSaleRequest(id: string): Promise<false | any> {
    return new Promise((res, rej) => {
        determineDataSource()
            .then(source => {
                source.sales.get(id)
                    .then(async sale => {
                        let uuid = null;
                        let clientId = id;

                        if(!id.includes("-")) {
                            uuid = id;
                            clientId = sale.clientId as string;
                        }

                        // Prepare the results
                        const results: any = {
                            id            : clientId,
                            linkedTo      : (sale.linkedTo ? sale.linkedTo : uuid),
                            register      : sale.registerId,
                            user          : sale.userId,
                            customer      : sale.customerId,
                            status        : sale.status,
                            invoiceId     : sale.invoiceId,
                            note          : sale.notes.sale,
                            internalNote  : sale.notes.internal,
                            orderReference: sale.orderReference,
                            refundReason  : sale.refundReason,
                            discount      : sale.discount,
                            change        : sale.totalChange,
                            createdAt     : sale.createdAt,
                            actions       : sale.actions,
                            metaData      : sale.metaData,
                            isCancellable : sale.isCancellable,
                            items         : [],
                            payments      : [],
                        };

                        // Prepare the payments
                        const payments = sale.payments;

                        for(let i = 0; i < payments.length; i++) {
                            const payment = payments[i];

                            if(payment.paymentMethodId === "account") {
                                continue; // Don't send this to the server
                            }

                            let metadata = null;

                            if(payment.metaData) {
                                metadata = payment.metaData;
                            } else if(payment.metadata) {
                                metadata = payment.metadata;
                            }

                            results.payments.push({
                                method     : payment.paymentMethodId,
                                amount     : payment.amount,
                                rounding   : payment.rounding,
                                cashout    : payment.cashout,
                                status     : payment.status.toUpperCase(),
                                register   : payment.registerId,
                                processTime: payment.processTime,
                                metaData   : metadata,
                                receipt    : payment.receipt,
                                subtype    : payment.subtype,
                                cancelledAt: typeof payment.cancelledAt === "string" ?
                                    new Date(payment.cancelledAt) :
                                    null,
                            });
                        }

                        // Prepare the items
                        const items = sale.items;

                        for(let i = 0; i < items.length; i++) {
                            const product = items[i];

                            if(product.item.type === "Surcharge") {
                                results.items.push({
                                    user     : (product as any).userId,
                                    note     : "",
                                    total    : product.totalPrice,
                                    surcharge: {
                                        taxRate: product.taxRate,
                                    },
                                    metaData: Object.keys(product.metaData).length ? product.metaData : undefined,
                                });
                                continue;
                            }

                            let taxRate;

                            if(product.taxRate === null) {
                                taxRate = product.taxRate;
                            } else {
                                try {
                                    const prod = await source.products.get(product.item.id);

                                    taxRate = prod.tax_rate?.uuid ?? null;
                                } catch {
                                    taxRate = null;
                                }
                            }

                            results.items.push({
                                user   : (product as any).userId,
                                note   : product.note,
                                total  : product.totalPrice,
                                product: product.item.type !== "GiftCard" ? {
                                    id        : product.item.id,
                                    quantity  : product.quantity,
                                    contains  : formatSaleItemContains(product.products),
                                    promotions: product.promotions.map(promotion => ({
                                        // Some promotions may have a criteria number attached as well
                                        id            : promotion.promotion.split("|")[0],
                                        quantity      : promotion.quantity,
                                        rebateAmount  : promotion.rebateAmount,
                                        rebateQuantity: promotion.rebateQuantity,
                                        savings       : promotion.savings,
                                    })),
                                    taxRate     : taxRate,
                                    caseQuantity: product.caseQuantity,
                                    discount    : product.discount,
                                    loyaltyValue: product.loyaltyValue,
                                } : null,
                                giftCard: product.item.type === "GiftCard" ? {
                                    id    : product.item.id,
                                    code  : product.item.code,
                                    expiry: product.item.expiry,
                                    source: product.item.source,
                                } : null,
                                metaData: Object.keys(product.metaData).length ? product.metaData : undefined,
                            });
                        }

                        res(results);
                    })
                    .catch(err => {
                        console.error(`Could not transform the sale into a request ${id}`, err);

                        if(err instanceof NotFoundException) {
                            // Nick and Travis discussion 2018-08-15
                            res(false);

                            return;
                        }

                        rej(err);
                    });
            });
    });
}

/*
function formatSaleItemProduct(product) {
    const result = {
        id      : product.item.id,
        type    : product.item.type,
        quantity: product.quantity,
        contains: [],
    };

    // Add gift card fields
    if (product.item.code) {
        result.code = product.item.code;
    }
    if (product.item.amount) {
        result.amount = product.item.amount;
    }
    if (product.item.code) {
        result.expiry = product.item.expiry;
    }

    for (let i = 0; i < product.products.length; i++) {
        const prod = product.products[i];

        result.contains.push(formatSaleItemProduct(prod));
    }

    return result;
}
*/

/**
 * Get the payment methods for the specified register (or all the payment methods if no register specified)
 * NOTE: I'm not sure if this is the right location to put this, but couldn't find any better location
 */
export async function getPaymentMethods(register: null | string): Promise<Array<LocalDatabasePaymentMethod>> {
    const source = await determineDataSource();

    if(register === null) {
        return source.paymentMethods.all();
    }

    const methods: Array<LocalDatabasePaymentMethod> = [];
    const promises = [];
    const reg = await source.registers.get(register);

    for(let i = 0, l = reg.payment_methods.length; i < l; i++) {
        promises.push(
            source.paymentMethods.get(reg.payment_methods[i])
                .then(method => {
                    methods.push(method);
                })
                .catch(() => {
                    // Ignore
                })
        );
    }

    await Promise.all(promises);

    return methods;
}

/**
 * Clear the cash calculator values
 */
export function clearStoredCashCalculator(): Promise<void> {
    setOption("register_closure_default_note", "");

    return determineDataSource()
        .then(source => {
            return source.paymentMethods.all();
        })
        .then(paymentMethods => {
            for(let i = 0, l = paymentMethods.length; i < l; i++) {
                sessionStorage.removeItem(paymentMethods[i].uuid);
            }
        })
        .catch(err => {
            console.error("Failed to clear stored cash calculator values", err);
        });
}
